/// <amd-module name="Wiki/Overview/Sources/WikiPageSource" />

import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import * as VCContracts from "Wiki/Clients/VC/Contracts/Git";
import { WikiPage, WikiPageMoveResponse, WikiPageResponse, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { getWikiClient, IWikiRestClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import { RepoConstants } from "Wiki/ViewCommon/Constants";
import { WikiPageInfo, WikiPagesOnlyDataProviderData } from "Wiki/ViewCommon/Contracts";
import { flattenWikiPage } from "Wiki/ViewCommon/Utils/PageHelper";
import { getVersionFromBranchName } from "Wiki/ViewCommon/Utils/WikiHelper";

export class WikiPageSource {
    private _wikiRestClient: IWikiRestClient;

    constructor(pageContext: IVssPageContext) {
        this._wikiRestClient = getWikiClient(pageContext);
    }

    public getAllPages(wiki: WikiV2, version: VCContracts.GitVersionDescriptor): Promise<WikiPage[]> {
        return this.getPageWithHierarchy(wiki, version, RepoConstants.RootPath);
    }

    public getPageWithHierarchy(wiki: WikiV2, version: VCContracts.GitVersionDescriptor, path: string): Promise<WikiPage[]> {
        // Task 1456983: [Improvement scope:] Server sends a tree hierarchy to client that we flatten and then create a tree structure
        return this._wikiRestClient.getPage(wiki.projectId, wiki.id, path, VCContracts.VersionControlRecursionType.Full, version).then(response => {
            return flattenWikiPage(response.page);
        });
    }

    public getPageChildren(wiki: WikiV2, version: VCContracts.GitVersionDescriptor, page: WikiPage): Promise<WikiPage[]> {
        if (!page.isParentPage) {
            return Promise.resolve([]);
        }

        return this._wikiRestClient
            .getPage(wiki.projectId, wiki.id, page.path, VCContracts.VersionControlRecursionType.OneLevel, version)
            .then(response => {
                return response && response.page ? response.page.subPages : [];
            });
    }

    public getPageInfo(wiki: WikiV2, pagePath: string, version: VCContracts.GitVersionDescriptor): Promise<WikiPageInfo> {
        return this._wikiRestClient
            .getPage(wiki.projectId, wiki.id, pagePath, VCContracts.VersionControlRecursionType.None, version, true)
            .then((pageResponse: WikiPageResponse) => {
                return {
                    ...pageResponse.page,
                    version: pageResponse.eTag[0]
                };
            });
    }

    public getInitialTreeHierarchy(
        pageContext: IVssPageContext,
        wiki: WikiV2,
        version: VCContracts.GitVersionDescriptor,
        pagePath: string
    ): Promise<WikiPagesOnlyDataProviderData | undefined> {
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const propertyBag = {
            wikiId: wiki.id,
            wikiVersion: getVersionFromBranchName(version.version),
            pagePath: pagePath
        };
        return contributionService.getDataAsync<WikiPagesOnlyDataProviderData>("ms.vss-wiki-web.wiki-pages-only-data-provider", propertyBag);
    }

    public movePage(
        wiki: WikiV2,
        version: VCContracts.GitVersionDescriptor,
        newPagePath: string,
        originalPagePath: string,
        newPageOrder: number,
        comment: string
    ): Promise<WikiPageMoveResponse> {
        return this._wikiRestClient.createPageMove(
            {
                path: originalPagePath,
                newPath: newPagePath,
                newOrder: newPageOrder && newPageOrder < 0 ? 0 : newPageOrder
            },
            wiki.projectId,
            wiki.id,
            comment,
            version
        );
    }

    public reorderPage(wiki: WikiV2, version: VCContracts.GitVersionDescriptor, path: string, newOrder: number): Promise<WikiPage> {
        return this._wikiRestClient
            .createPageMove(
                {
                    path,
                    newOrder,
                    newPath: path
                },
                wiki.projectId,
                wiki.id,
                undefined,
                version
            )
            .then(response => {
                return response.pageMove.page;
            });
    }

    /**
     * Create or update the wiki page. This is needed here to handle
     * the embed work item in page on creating wit on text selection.
     */
    public createOrUpdatePage(
        wiki: WikiV2,
        path: string,
        content: string,
        version: string,
        commitMessage?: string,
        versionDescriptor?: VCContracts.GitVersionDescriptor
    ): Promise<WikiPageResponse> {
        return this._wikiRestClient.createOrUpdatePage(
            {
                content
            },
            wiki.projectId,
            wiki.id,
            path,
            version,
            commitMessage,
            versionDescriptor
        );
    }
}
